import { Box, Button, Checkbox, FormControl, FormControlLabel, FormHelperText, Grid, Paper, TextField, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { TxtDocumentoCPFCNPJ } from '../../components/formControls/TxtDocumentoCPFCNPJ';
import { TxtTelefone } from '../../components/formControls/TxtTelefone';
import { TxtTipoUsuario } from '../../components/formControls/TxtTipoUsuario';
import { ISusuario, Usuario, UsuarioDTO } from '../../types/usuario/Usuario';
import errorFormat from '../../utils/errorFormat';
import { useAddUsuarioMutation, useGetUsuarioQuery } from './usuarioApiSlice';
import { validarSenhaForte } from '../../utils/validarSenhaForte';

export const UsuarioCreate = () => {
  const [usuario, SetUsuario] = useState<Usuario>(ISusuario);
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const [createUsuario, status] = useAddUsuarioMutation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [senha, setSenha] = useState<string>('');
  const [confirmaSenha, setConfirmaSenha] = useState<string>('');
  type TipoUsuario = 'ADMINISTADOR' | 'USUARIO' | null;

  //verificar se for edicao ele pega o id e faz a consulta
  const id = useParams().id || '';
  const { data, isFetching, error } = useGetUsuarioQuery(id);
  useEffect(() => {
    if (data) {
      SetUsuario(data);
    }
  }, [data, error, isFetching]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const cursorPosition = e.target.selectionStart;

    SetUsuario(prev => ({
      ...prev,
      [name]: value.toUpperCase()
    }));

    // Aguarde até que o DOM seja atualizado
    setTimeout(() => {
      const input = e.target;
      input.setSelectionRange(cursorPosition, cursorPosition);
    }, 0);
  };

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const parms: UsuarioDTO = {
      id: usuario.id || '',
      nome: usuario.nome || '',
      login: usuario.login || '',
      cpf: usuario.cpf || '',
      administradorOuvidoria: usuario.administradorOuvidoria || false,
      administradorSite: usuario.administradorSite || false,
      tipo: usuario.tipo,
      telefone: usuario.telefone || '',
      senha: senha,
      ativo: usuario.ativo
    };
    if (senha !== confirmaSenha) {
      enqueueSnackbar('Senhas diferentes!', { variant: 'error' });
      return;
    }
    if (senha && !validarSenhaForte(senha)) {
      enqueueSnackbar('Senha fraca!', { variant: 'error' });
      return;
    }

    await createUsuario(parms);
  }

  useEffect(() => {
    if (status.isError) {
      enqueueSnackbar('Registro não incluído!', { variant: 'error' });
      const erroReturn: string = errorFormat(status);
      enqueueSnackbar(erroReturn, { variant: 'error' });
    }

    if (status.isSuccess) {
      enqueueSnackbar('Registro incluído!', { variant: 'success' });
      navigate(-1);
      // setGrupo(ISgrupo);
    }
  }, [status]);

  return (
    <>
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          justifyContent: 'center',
          mt: 4
        }}
      >
        <Paper
          elevation={4}
          sx={{
            width: '100%',
            maxWidth: '500px',
            p: 3,
            borderRadius: 3
          }}
        >
          <Typography
            variant="h6"
            component="h1"
            sx={{
              mb: 5,
              fontWeight: 600,
              textAlign: 'center'
            }}
          >
            {usuario.id ? 'Edição de Usuário' : 'Cadastro de Usuário'}
          </Typography>

          <form onSubmit={handleSubmit} method="POST">
            <Grid container spacing={2}>
              {/* Administrador Site */}
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={usuario.ativo || false}
                      onChange={e =>
                        SetUsuario(prev => ({
                          ...prev,
                          ativo: e.target.checked
                        }))
                      }
                      name="ativo"
                      color="primary"
                    />
                  }
                  label="Usuário ativo?"
                />
              </Grid>

              {/* CPF */}
              <Grid item xs={12} md={6}>
                <TxtDocumentoCPFCNPJ value={usuario.cpf || ''} onChange={val => SetUsuario(prev => ({ ...prev, cpf: val }))} disabled={false} required={true} />
              </Grid>

              {/* Nome */}
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <TextField required label="Nome" name="nome" onChange={handleChange} size="small" inputProps={{ maxLength: 50 }} value={usuario.nome} />
                  <FormHelperText>{usuario.nome?.length || 0}/50 caracteres</FormHelperText>
                </FormControl>
              </Grid>

              {/* Telefone */}
              <Grid item xs={12}>
                <TxtTelefone value={usuario.telefone || ''} onChange={val => SetUsuario(prev => ({ ...prev, telefone: val }))} required />
              </Grid>
              {/* Categoria */}
              <Grid item xs={12}>
                <TxtTipoUsuario
                  value={usuario.tipo ?? ''}
                  onChange={e =>
                    SetUsuario(prev => ({
                      ...prev,
                      tipo: e.target.value as TipoUsuario
                    }))
                  }
                  required
                  helperText={`${usuario.tipo?.length ?? 0}/50 caracteres`}
                />
              </Grid>

              {/* Login */}
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <TextField required label="Login" name="login" onChange={handleChange} size="small" inputProps={{ maxLength: 50 }} value={usuario.login} disabled={!!usuario.id} />
                  <FormHelperText>{usuario.login?.length || 0}/50 caracteres</FormHelperText>
                </FormControl>
              </Grid>

              {!usuario.id && (
                <>
                  {/* Senha */}
                  <Grid item xs={12}>
                    <FormControl fullWidth>
                      <TextField
                        required
                        label="Senha"
                        name="senha"
                        type="password"
                        onChange={e => setSenha(e.target.value)}
                        size="small"
                        inputProps={{ maxLength: 100 }}
                        value={senha}
                        error={senha !== confirmaSenha || (!!senha && !validarSenhaForte(senha))}
                        helperText={
                          senha && !validarSenhaForte(senha) ? 'Senha fraca: mínimo 8 caracteres, incluindo maiúscula, minúscula, número e caractere especial' : `${senha?.length || 0}/100 caracteres`
                        }
                      />
                    </FormControl>
                  </Grid>

                  {/* Confirmar Senha */}
                  <Grid item xs={12}>
                    <FormControl fullWidth>
                      <TextField
                        required
                        label="Confirmar Senha"
                        name="confirmaSenha"
                        type="password"
                        onChange={e => setConfirmaSenha(e.target.value)}
                        size="small"
                        inputProps={{ maxLength: 100 }}
                        value={confirmaSenha}
                        error={senha !== confirmaSenha}
                      />
                      <FormHelperText>{confirmaSenha?.length || 0}/10 caracteres</FormHelperText>
                    </FormControl>
                  </Grid>
                </>
              )}

              {/* Administrador Site */}
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={usuario.administradorSite || false}
                      onChange={e =>
                        SetUsuario(prev => ({
                          ...prev,
                          administradorSite: e.target.checked
                        }))
                      }
                      name="administradorSite"
                      color="primary"
                    />
                  }
                  label="Administrador do Site"
                />
              </Grid>

              {/* Administrador Ouvidoria */}
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={usuario.administradorOuvidoria || false}
                      onChange={e =>
                        SetUsuario(prev => ({
                          ...prev,
                          administradorOuvidoria: e.target.checked
                        }))
                      }
                      name="administradorOuvidoria"
                      color="primary"
                    />
                  }
                  label="Administrador da Ouvidoria"
                />
              </Grid>

              {/* Botões */}
              <Grid item xs={12}>
                <Box display="flex">
                  <Button type="submit" variant="contained" color="secondary" size="large">
                    Gravar
                  </Button>

                  <Button variant="outlined" size="large" onClick={() => navigate(-1)}>
                    Voltar
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </form>
        </Paper>
      </Box>
    </>
  );
};
